import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { API_AUTH } from '../api/paths';

/**
 * Session state.
 *
 * The session lives in an httpOnly cookie, so this hook never sees or stores a
 * token — it only asks the server "am I signed in?" and relies on the browser
 * to attach the cookie. That is why every request uses `credentials: 'include'`.
 */
export interface AuthUser {
  email: string;
}

export interface AuthState {
  user: AuthUser | null;
  /** True until the initial session check resolves. */
  checking: boolean;
}

/** Derived from the build's mount prefix — never an absolute `/api/auth`. */
const AUTH_BASE = API_AUTH;

async function authRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${AUTH_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init?.headers ?? {}) },
  });

  if (!response.ok) {
    let code = 'HTTP_ERROR';
    let message = `Request failed (${response.status}).`;
    try {
      const body = (await response.json()) as { error?: { code: string; message: string } };
      if (body.error) {
        code = body.error.code;
        message = body.error.message;
      }
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(code, message, response.status);
  }

  return (await response.json()) as T;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ user: null, checking: true });

  // Resolve the existing session (if any) on first paint.
  useEffect(() => {
    let cancelled = false;
    authRequest<{ user: AuthUser }>('/me')
      .then((r) => {
        if (!cancelled) setState({ user: r.user, checking: false });
      })
      .catch(() => {
        if (!cancelled) setState({ user: null, checking: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await authRequest<{ user: AuthUser }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setState({ user: r.user, checking: false });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authRequest('/logout', { method: 'POST' });
    } finally {
      setState({ user: null, checking: false });
    }
  }, []);

  /** Called when a data request comes back 401 — the session expired mid-use. */
  const onSessionExpired = useCallback(() => {
    setState({ user: null, checking: false });
  }, []);

  return { ...state, login, logout, onSessionExpired };
}
